import React, { useState } from "react";
import Navbar from "../components/Navbar";
import useFooterConfig from "../hooks/useFooterConfig";
import ScrollReveal from "../components/ScrollReveal";
import SEO from "../components/SEO";
import {
  BsGeoAlt,
  BsTelephone,
  BsFacebook,
  BsGlobe,
  BsSend,
  BsCheckCircleFill,
  BsClock,
  BsMessenger,
} from "react-icons/bs";
import { SiZalo } from "react-icons/si";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Vui lòng nhập họ tên";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Vui lòng nhập số điện thoại";
    } else if (!/^[0-9]{10,11}$/.test(formData.phone.replace(/\s/g, ""))) {
      newErrors.phone = "Số điện thoại không hợp lệ";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Vui lòng chọn chủ đề";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Vui lòng nhập nội dung tin nhắn";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Nội dung tin nhắn quá ngắn (tối thiểu 10 ký tự)";
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    console.log("Form submitted:", formData);
    setSubmitted(true);
    setFormData({
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    });

    setTimeout(() => {
      setSubmitted(false);
    }, 5000);
  };

  const { footerConfig } = useFooterConfig();

  // Dynamic contact info
  const liveContact = {
    address:
      footerConfig?.address ||
      "44 Đ. 24, Linh Đông, Thủ Đức, Thành phố Hồ Chí Minh",
    mapsLink:
      footerConfig?.mapsLink ||
      "https://www.google.com/maps/search/?api=1&query=TAHA+SHOES+44+D.+24+Linh+Dong+Thu+Duc",
    phone: footerConfig?.phone || "0765.109.784",
    zalo: footerConfig?.zalo || "0765.109.784",
  };

  return (
    <div 
      className="bg-white dark:bg-slate-950 min-h-screen flex flex-col selection:bg-blue-100 dark:selection:bg-blue-900/30 selection:text-blue-900 dark:selection:text-blue-200 transition-colors duration-300"
      style={{ 
        zoom: "0.8",
        MozTransform: "scale(0.8)",
        MozTransformOrigin: "top center"
      }}
    >
      <Navbar />
      <SEO
        title="Liên hệ với TAHA SHOES"
        description="Liên hệ với TAHA SHOES để được tư vấn và hỗ trợ tốt nhất về các dòng giày thể thao hiệu suất cao."
      />


      <main className="flex-grow pt-16 md:pt-40 pb-12 sm:pb-16 overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-full h-[800px] pointer-events-none -z-10 overflow-hidden">
          <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-50 dark:bg-blue-900/10 rounded-full blur-[120px] opacity-60 dark:opacity-40 animate-pulse" />
          <div className="absolute top-[20%] right-[-5%] w-[400px] h-[400px] bg-indigo-50 dark:bg-indigo-900/10 rounded-full blur-[100px] opacity-50 dark:opacity-30" />
        </div>

        {/* Hero Section */}
        <section className="px-4 mb-6 sm:mb-10">
          <div className="max-w-7xl mx-auto">
            <ScrollReveal variants="fade-up">
              <div className="text-center relative">
                <div className="inline-block px-3 py-1 mb-4 sm:mb-8 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-[10px] sm:text-sm font-black tracking-widest uppercase border border-blue-100 dark:border-blue-900/30">
                  Kết nối với chúng tôi
                </div>
                <h1 className="text-2xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-4 sm:mb-6 tracking-tighter leading-tight uppercase">
                  LIÊN{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                    HỆ
                  </span>
                </h1>
                <div className="w-16 sm:w-24 h-1 sm:h-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 mx-auto rounded-full mb-6 sm:mb-8 shadow-lg" />
                <p className="max-w-2xl mx-auto text-gray-900 dark:text-slate-300 text-sm sm:text-lg md:text-xl leading-relaxed font-bold">
                  Chúng tôi luôn sẵn sàng lắng nghe và hỗ trợ bạn mọi lúc.{" "}
                  <br className="hidden sm:block" />
                  Hãy gửi tin nhắn hoặc ghé thăm chúng tôi để có trải nghiệm tốt
                  nhất.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-8 sm:py-12 px-4 mb-12 sm:mb-24">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[
                {
                  icon: BsGeoAlt,
                  title: "Địa chỉ",
                  content: liveContact.address,
                  color: "from-blue-500 to-blue-700",
                  sub: "Cửa hàng chính thức",
                  link: liveContact.mapsLink,
                },
                {
                  icon: BsTelephone,
                  title: "Hotline",
                  content: liveContact.phone,
                  link: `tel:${liveContact.phone.replace(/\./g, "")}`,
                  color: "from-indigo-600 to-blue-800",
                  sub: "Hỗ trợ 24/7",
                },
                {
                  icon: SiZalo,
                  title: "Zalo",
                  content: liveContact.zalo,
                  link: `https://zalo.me/${liveContact.zalo.replace(/\./g, "")}`,
                  color: "from-blue-500 to-cyan-600",
                  sub: "Chat với chúng tôi",
                },
                {
                  icon: BsMessenger,
                  title: "Messenger",
                  content: "tahashoes.vn",
                  link: "https://m.me/tahashoes.vn",
                  color: "from-blue-600 to-blue-400",
                  sub: "Hỗ trợ Facebook",
                },
              ].map((item, i) => (
                <ScrollReveal key={i} variants="scale-up" delay={i * 0.1}>
                  <div className="relative group h-full">
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${item.color} rounded-[2.5rem] opacity-0 group-hover:opacity-10 transition-all duration-500 blur-xl -z-10`}
                    />
                    <div className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-2xl sm:rounded-[2rem] shadow-xl hover:shadow-2xl transition-all duration-500 h-full border border-gray-100 dark:border-slate-800 flex flex-col items-center text-center group-hover:-translate-y-2">
                      {item.link ? (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`w-12 h-12 sm:w-20 sm:h-20 bg-gradient-to-br ${item.color} rounded-full flex items-center justify-center text-white mb-4 sm:mb-8 shadow-lg shadow-blue-200 dark:shadow-blue-900/20 rotate-3 group-hover:rotate-0 transition-transform active:scale-90`}
                        >
                          <item.icon size={24} className="sm:hidden" />
                          <item.icon size={40} className="hidden sm:block" />
                        </a>
                      ) : (
                        <div
                          className={`w-12 h-12 sm:w-20 sm:h-20 bg-gradient-to-br ${item.color} rounded-full flex items-center justify-center text-white mb-4 sm:mb-8 shadow-lg shadow-blue-200 dark:shadow-blue-900/20 rotate-3 group-hover:rotate-0 transition-transform`}
                        >
                          <item.icon size={24} className="sm:hidden" />
                          <item.icon size={40} className="hidden sm:block" />
                        </div>
                      )}
                      <p className="text-[8px] sm:text-[10px] text-gray-400 dark:text-slate-500 font-black uppercase tracking-[0.2em] mb-1 sm:mb-2">
                        {item.sub}
                      </p>
                      <h3 className="text-lg sm:text-2xl font-black text-gray-900 dark:text-white mb-2 sm:mb-4 uppercase">
                        {item.title}
                      </h3>
                      {item.link ? (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-900 dark:text-slate-300 font-bold leading-relaxed hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-xs sm:text-base"
                        >
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-gray-900 dark:text-slate-300 font-bold leading-relaxed text-xs sm:text-base">
                          {item.content}
                        </p>
                      )}
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Form and Social Section */}
        <section className="py-10 sm:py-16 px-4 bg-gray-50 dark:bg-slate-900/50 relative overflow-hidden">
          {/* Section Pattern */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(#2563eb 0.5px, transparent 0.5px)",
              backgroundSize: "30px 30px",
            }}
          />

          <div className="max-w-6xl mx-auto relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
              {/* Form Side */}
              <ScrollReveal variants="slide-right">
                <div className="bg-white dark:bg-slate-900 p-4 sm:p-10 rounded-2xl sm:rounded-[3rem] shadow-2xl shadow-blue-900/5 relative overflow-hidden border border-gray-100 dark:border-slate-800">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 dark:bg-blue-900/10 rounded-full -translate-y-1/2 translate-x-1/2" />

                  <h2 className="text-lg sm:text-3xl font-black text-gray-900 dark:text-white mb-4 sm:mb-8 uppercase tracking-tight">
                    Gửi tin nhắn <br />
                    <span className="text-blue-600 dark:text-blue-400">
                      cho{" "}
                      <span className="text-gray-900 dark:text-white transition-colors">
                        TAHA
                      </span>{" "}
                      <span className="text-red-600">SHOES</span>
                    </span>
                  </h2>

                  {submitted && (
                    <div className="mb-4 sm:mb-10 bg-blue-600 p-3 sm:p-6 rounded-xl sm:rounded-3xl text-white animate-fade-in shadow-xl flex items-center gap-2 sm:gap-4">
                      <BsCheckCircleFill size={20} className="sm:hidden" />
                      <BsCheckCircleFill
                        size={32}
                        className="hidden sm:block"
                      />
                      <div>
                        <p className="font-black uppercase text-[9px] sm:text-sm tracking-widest mb-0.5 sm:mb-1">
                          Thành công!
                        </p>
                        <p className="font-bold text-[11px] sm:text-base">
                          Cảm ơn bạn. Chúng tôi sẽ sớm liên hệ lại!
                        </p>
                      </div>
                    </div>
                  )}

                  <form
                    onSubmit={handleSubmit}
                    className="space-y-3 sm:space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
                      <div className="space-y-1 sm:space-y-2">
                        <label className="text-[9px] sm:text-xs font-black uppercase tracking-widest text-gray-900 dark:text-slate-300 ml-2 transition-colors">
                          Họ và tên *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={`w-full px-4 sm:px-6 py-2.5 sm:py-4 bg-gray-50 dark:bg-slate-800 border-2 rounded-xl sm:rounded-2xl focus:outline-none focus:bg-white dark:focus:bg-slate-700 transition-all font-bold text-[13px] sm:text-base text-gray-900 dark:text-white ${
                            errors.name
                              ? "border-red-500"
                              : "border-gray-50 dark:border-slate-800 focus:border-blue-600 dark:focus:border-blue-500"
                          }`}
                          placeholder="Nguyễn Văn A"
                        />
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <label className="text-[9px] sm:text-xs font-black uppercase tracking-widest text-gray-900 dark:text-slate-300 ml-2 transition-colors">
                          Số điện thoại *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`w-full px-4 sm:px-6 py-2.5 sm:py-4 bg-gray-50 dark:bg-slate-800 border-2 rounded-xl sm:rounded-2xl focus:outline-none focus:bg-white dark:focus:bg-slate-700 transition-all font-bold text-[13px] sm:text-base text-gray-900 dark:text-white ${
                            errors.phone
                              ? "border-red-500"
                              : "border-gray-50 dark:border-slate-800 focus:border-blue-600 dark:focus:border-blue-500"
                          }`}
                          placeholder="07xx xxx xxx"
                        />
                      </div>
                    </div>

                    <div className="space-y-1 sm:space-y-2">
                      <label className="text-[9px] sm:text-xs font-black uppercase tracking-widest text-gray-900 dark:text-slate-300 ml-2 transition-colors">
                        Chủ đề *
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className={`w-full px-4 sm:px-6 py-2.5 sm:py-4 bg-gray-50 dark:bg-slate-800 border-2 rounded-xl sm:rounded-2xl focus:outline-none focus:bg-white dark:focus:bg-slate-700 transition-all font-bold text-[13px] sm:text-base text-gray-900 dark:text-white appearance-none ${
                          errors.subject
                            ? "border-red-500"
                            : "border-gray-50 dark:border-slate-800 focus:border-blue-600 dark:focus:border-blue-500"
                        }`}
                      >
                        <option value="">-- Chọn chủ đề --</option>
                        <option value="product">Thông tin sản phẩm</option>
                        <option value="order">Đơn hàng</option>
                        <option value="warranty">Bảo hành</option>
                        <option value="other">Ý kiến đóng góp</option>
                      </select>
                    </div>

                    <div className="space-y-1 sm:space-y-2">
                      <label className="text-[9px] sm:text-xs font-black uppercase tracking-widest text-gray-900 dark:text-slate-300 ml-2 transition-colors">
                        Nội dung *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="3"
                        className={`w-full px-4 sm:px-6 py-2.5 sm:py-4 bg-gray-50 dark:bg-slate-800 border-2 rounded-xl sm:rounded-2xl focus:outline-none focus:bg-white dark:focus:bg-slate-700 transition-all font-bold text-[13px] sm:text-base text-gray-900 dark:text-white resize-none ${
                          errors.message
                            ? "border-red-500"
                            : "border-gray-50 dark:border-slate-800 focus:border-blue-600 dark:focus:border-blue-500"
                        }`}
                        placeholder="Bạn cần hỗ trợ gì?"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-3.5 sm:py-5 rounded-xl sm:rounded-2xl font-black uppercase text-[11px] sm:text-base tracking-[0.2em] shadow-xl shadow-blue-200 hover:shadow-2xl hover:scale-[1.02] transform transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3"
                    >
                      <BsSend size={16} className="sm:hidden" />
                      <BsSend size={20} className="hidden sm:block" />
                      Gửi tin nhắn ngay
                    </button>
                  </form>
                </div>
              </ScrollReveal>

              {/* Info & Social Side */}
              <div className="flex flex-col gap-6 sm:gap-12">
                <ScrollReveal variants="slide-left" delay={0.2}>
                  <div className="bg-gradient-to-br from-blue-600 to-indigo-900 p-4 sm:p-10 rounded-2xl sm:rounded-[3rem] text-white shadow-2xl relative overflow-hidden group">
                    {/* Decorative circles */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none group-hover:scale-125 transition-transform duration-700" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none" />

                    <h2 className="text-lg sm:text-4xl font-black mb-4 sm:mb-8 leading-tight uppercase tracking-tight">
                      KẾT NỐI TRỰC TIẾP
                    </h2>

                    <div className="space-y-4 sm:space-y-8">
                      <div className="flex items-center gap-3 sm:gap-8 group/item">
                        <a
                          href="https://zalo.me/0765109784"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 sm:w-16 sm:h-16 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-md border border-white/20 group-hover/item:bg-white group-hover/item:text-blue-600 transition-all duration-300 shadow-xl group-hover/item:rotate-12 active:scale-90"
                        >
                          <SiZalo size={20} className="sm:hidden" />
                          <SiZalo size={32} className="hidden sm:block" />
                        </a>
                        <div className="flex flex-col">
                          <p className="text-[7px] sm:text-[10px] text-blue-200 font-black uppercase tracking-[0.2em] mb-0.5 sm:mb-1">
                            Zalo Shop (TAHA{" "}
                            <span className="text-red-500">SHOES</span>)
                          </p>

                          <a
                            href={
                              liveContact.zalo
                                ? `https://zalo.me/${liveContact.zalo.replace(/\./g, "")}`
                                : "#"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-base sm:text-3xl font-black tracking-tight hover:text-blue-200 transition-colors"
                          >
                            {liveContact.zalo}
                          </a>
                        </div>
                      </div>

                      <a
                        href="https://www.facebook.com/tahashoes.vn"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 sm:gap-8 group/item cursor-pointer"
                      >
                        <div className="w-10 h-10 sm:w-16 sm:h-16 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-md border border-white/20 group-hover/item:bg-[#1877f2] transition-all duration-300 shadow-xl group-hover/item:rotate-12">
                          <BsFacebook size={20} className="sm:hidden" />
                          <BsFacebook size={32} className="hidden sm:block" />
                        </div>
                        <div className="flex flex-col">
                          <p className="text-[7px] sm:text-[10px] text-blue-200 font-black uppercase tracking-[0.2em] mb-0.5 sm:mb-1">
                            Fanpage Facebook
                          </p>
                          <p className="text-base sm:text-3xl font-black tracking-tight">
                            TAHA <span className="text-red-600">SHOES</span>
                          </p>
                        </div>
                      </a>

                      <div className="flex items-center gap-3 sm:gap-8 group/item">
                        <div className="w-10 h-10 sm:w-16 sm:h-16 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-md border border-white/20 group-hover/item:bg-blue-400 transition-all duration-300 shadow-xl group-hover/item:rotate-12">
                          <BsGlobe size={20} className="sm:hidden" />
                          <BsGlobe size={32} className="hidden sm:block" />
                        </div>
                        <div className="flex flex-col">
                          <p className="text-[7px] sm:text-[10px] text-blue-200 font-black uppercase tracking-[0.2em] mb-0.5 sm:mb-1">
                            Website chính thức
                          </p>
                          <p className="text-base sm:text-3xl font-black tracking-tight underline cursor-pointer decoration-2 underline-offset-8">
                            tahashoes.vn
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 sm:mt-10 pt-4 sm:pt-8 border-t border-white/10 flex items-center gap-3 sm:gap-6">
                      <div className="w-9 h-9 sm:w-14 sm:h-14 bg-white rounded-xl sm:rounded-2xl flex items-center justify-center text-blue-800 shadow-lg">
                        <BsClock size={18} className="sm:hidden" />
                        <BsClock size={28} className="hidden sm:block" />
                      </div>
                      <div>
                        <p className="text-[7px] sm:text-xs font-black uppercase tracking-widest text-blue-200 mb-0.5 sm:mb-1">
                          Thời gian phục vụ
                        </p>
                        <p className="text-xs sm:text-lg font-bold">
                          {footerConfig?.workingHours || "08:30 - 22:00"}
                        </p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>

                {/* Map Part */}
                <ScrollReveal variants="fade-up" delay={0.4}>
                  <div className="rounded-2xl sm:rounded-[4rem] overflow-hidden shadow-2xl border-4 border-white dark:border-slate-800 relative h-[240px] sm:h-[320px] group cursor-pointer">
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=TAHA+SHOES+44+D.+24+Linh+Dong+Thu+Duc"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute inset-0 z-10"
                    >
                      <span className="sr-only">
                        Xem vị trí trên Google Maps
                      </span>
                    </a>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4326.654661155155!2d106.73975741102659!3d10.851072889257646!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x317527ff01d0059%3A0x5009f2a9adbbc880!2sTAHA%20SHOES!5e1!3m2!1svi!2s!4v1773301664595!5m2!1svi!2s"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      title="TAHA SHOES Location"
                      className="grayscale-[0.5] group-hover:grayscale-0 transition-all duration-700 scale-110 group-hover:scale-100"
                    ></iframe>
                    <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 p-3 sm:p-4 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md rounded-xl sm:rounded-2xl border border-white/50 dark:border-slate-800 shadow-xl opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 z-20 pointer-events-none">
                      <p className="text-[10px] sm:text-xs font-black text-blue-600 dark:text-blue-400 uppercase mb-0.5 sm:mb-1 flex items-center gap-2">
                        <BsGeoAlt /> Vị trí của chúng tôi
                      </p>
                      <p className="text-gray-900 dark:text-white font-bold text-xs sm:text-sm">
                        {liveContact.address}
                      </p>
                      <p className="text-blue-500 text-[8px] sm:text-[10px] font-black uppercase tracking-widest mt-1 sm:mt-2 animate-pulse">
                        Nhấn để xem trên bản đồ
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Contact;
